import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import HeaderWithChildrenWrapper from 'shared/components/molecules/HeaderWithChildrenWrapper';
import LinkButton from 'shared/components/atoms/LinkButton';
import Button, { ButtonMainStyle } from 'shared/components/atoms/Button';
import { submitCease } from 'CustomerRequests/routes';
import { useDispatch, useSelector } from 'react-redux';
import { selectCompanyData } from 'User/selectors';
import { hasSSEAdminRole, hasSSEAccountManagerRole, hasSSEAdminWildcardRole } from 'User/selectors';
import { setActiveCustomer } from '../actions';
import { Link } from 'react-router-dom';
import { ceaseById } from 'CustomerRequests/routes';
import Alert, { Alerts } from 'shared/components/atoms/Alert';
import SpinnerWithText from 'shared/components/molecules/SpinnerWithText';
import CeaseState from '../components/CeaseState';
import { pages, useTrackPage } from 'shared/utils/trackPage';
import { getCustomerFromCompanyData } from 'CustomerRequests/utils/getCustomerFromCompanyData';
import Checkbox from 'shared/components/atoms/Checkbox';
import { useTablePaging } from 'shared/hooks/useTablePaging';
import { toCeaseDetails } from '../crud/ceaseCrud';
import { CeaseRequestDetailsResponse, CeaseRequestType } from '../types/cease';
import ServerSidePaginationBar from 'shared/components/molecules/ServerSidePaginationBar';
import { base as faqBase } from 'FAQ/routes';
import { buildIncludedIndex, getSubmittedByDisplayName } from '../utils/includedIndex';
import dayjs from 'dayjs';
import Dropdown, { IDropdownOption } from 'shared/components/molecules/Dropdown';
import { getTypeLabelFromCategory } from '../utils/requestType';
import { saveAs } from 'file-saver';
import buildFileNameWithDate from 'shared/utils/buildFileNameWithDate';
import { doRequest } from 'Request';

const ALL_TYPE_FILTER = 'All request types';
type CeaseTypeFilter = CeaseRequestType | typeof ALL_TYPE_FILTER;

const CeaseList: FC = () => {
  useTrackPage(pages.ceaseRequests);

  const dispatch = useDispatch();
  const companyData = useSelector(selectCompanyData);
  const { customerId } = getCustomerFromCompanyData(companyData);
  const isSSEAdmin = useSelector((state: any) => hasSSEAdminRole(state) || hasSSEAccountManagerRole(state) || hasSSEAdminWildcardRole(state));

  const hasMultipleCustomers = (companyData?.companies?.length ?? 0) > 1;
  const [showAllCustomers, setShowAllCustomers] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<CeaseTypeFilter>(ALL_TYPE_FILTER);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string>('');

  useEffect(() => {
    if (showAllCustomers && !hasMultipleCustomers) {
      setShowAllCustomers(false);
    }
  }, [hasMultipleCustomers, showAllCustomers]);

  const paging = useTablePaging({
    baseUrl: '/requests/cease',
    defaultPageSize: 10,
    enabled: showAllCustomers ? (companyData?.companies?.length ?? 0) > 0 : !!customerId,
    extraParams: showAllCustomers ? undefined : customerId ? { customer_id: customerId } : undefined,
    dataTransform: (res: any) => {
      const data = (res?.data || []) as CeaseRequestDetailsResponse[];
      return data.map(toCeaseDetails);
    },
  });

  useEffect(() => {
    if (!customerId || showAllCustomers) return;
    dispatch(setActiveCustomer(customerId));
  }, [customerId, dispatch, showAllCustomers]);

  const isInitialLoad = paging.isLoading && paging.fetchAttempts === 0;
  const isLoading = paging.isLoading;
  const ceases = paging.results;

  const typeFilterOptions: IDropdownOption[] = useMemo(
    () => [
      { label: ALL_TYPE_FILTER, value: ALL_TYPE_FILTER },
      { label: CeaseRequestType.CeaseRequest, value: CeaseRequestType.CeaseRequest },
      { label: CeaseRequestType.CeaseQuote, value: CeaseRequestType.CeaseQuote },
    ],
    []
  );

  const filteredCeases = useMemo(() => {
    if (typeFilter === ALL_TYPE_FILTER) return ceases;

    return ceases.filter(
      (c) => getTypeLabelFromCategory(c.category, c.subCategory) === typeFilter
    );
  }, [ceases, typeFilter]);

  const includedIndex = useMemo(() => buildIncludedIndex((paging.included || []) as any), [paging.included]);

  const formatDate = (value: string) => {
    if (!value) return '';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD/MM/YYYY') : value;
  };

  const formatCustomerReference = (value?: string) => {
    if (!value) return 'Not provided';
    return value.length > 20 ? `${value.slice(0, 20)}…` : value;
  };

  const exportAsCsv = async () => {
    setIsExporting(true);
    setExportError('');

    try {
      const exportResponse = await doRequest<{ data: CeaseRequestDetailsResponse[]; included?: any[] }>({
        path: '/requests/cease',
        params: showAllCustomers ? undefined : customerId ? { customer_id: customerId } : undefined,
      });

      const exportCeases = (exportResponse?.data || []).map(toCeaseDetails);
      const exportIncludedIndex = buildIncludedIndex((exportResponse?.included || []) as any);

      const ceasesToExport =
        typeFilter === ALL_TYPE_FILTER
          ? exportCeases
          : exportCeases.filter((c) => getTypeLabelFromCategory(c.category, c.subCategory) === typeFilter);

      const header = [
        'Cease reference',
        'Type',
        'Customer',
        'Customer reference',
        'IDN',
        'Requested cease date',
        'Submitted by',
        'Submitted date',
        'Cease reason',
      ];

      if (isSSEAdmin) {
        header.push('Integration', 'Status');
      }

    const escapeCsv = (value: string) => `"${String(value || '').replace(/"/g, '""')}"`;

      const rows = ceasesToExport.map((c) => {
        const row = [
          (c as any).snowReference || '{Pending}',
          getTypeLabelFromCategory(c.category, c.subCategory),
          c.customerName || '',
          c.customerReference || 'Not provided',
          c.idn || '',
          formatDate(c.requestedCeaseDate),
          getSubmittedByDisplayName(c as any, exportIncludedIndex),
          formatDate(c.submittedAt),
          c.ceaseReason || '',
        ];

        if (isSSEAdmin) {
          row.push((c as any).integrationStatus || 'Not synced', c.status || '');
        }

        return row;
      });

      const csv = [header, ...rows].map((row) => row.map((v) => escapeCsv(v)).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'data:text/csv;charset=utf-8,' });

      saveAs(blob, buildFileNameWithDate('Cease requests', 'csv'));
    } catch (error) {
      setExportError('Error exporting cease requests. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <HeaderWithChildrenWrapper header="Cease requests" compact={true}>
      <Container>
        <div className="d-flex align-items-start justify-content-between flex-wrap mb-3">
          <p className="text-muted mb-0 mr-3">
            View and track your cease requests.{' '}
            <Link to={faqBase} rel="noreferrer">
              Help &amp; FAQs
            </Link>
          </p>

          <div className="cease-controls">
            {hasMultipleCustomers && (
              <Checkbox
                className="cease-showAllCustomers"
                inputId="cease-showAllCustomers"
                label="Show requests for all customers"
                value={showAllCustomers}
                onChange={(checked) => setShowAllCustomers(checked)}
              />
            )}

            <div className="cease-type-filter">
              <Dropdown
                options={typeFilterOptions}
                placeholder="Select..."
                isClearable={false}
                value={{ label: typeFilter, value: typeFilter }}
                onChange={(selection) => setTypeFilter((selection?.value as CeaseTypeFilter) || ALL_TYPE_FILTER)}
              />
            </div>

            <LinkButton
              pathname={submitCease}
              buttonText="Submit cease"
              mainStyle={ButtonMainStyle.PrimaryRectangular}
              className="w-100"
            />
          </div>
        </div>

        {!customerId && <Alert alertType={Alerts.DANGER}>No customer selected.</Alert>}

        {!isInitialLoad && paging.error && <Alert alertType={Alerts.DANGER}>Error fetching cease requests.</Alert>}

        {isLoading && <SpinnerWithText text="Loading cease requests" size="large" />}

        {!isLoading && customerId && ceases.length === 0 && (
          <Alert alertType={Alerts.INFO}>No cease requests to display yet.</Alert>
        )}

        {!isLoading && customerId && ceases.length > 0 && filteredCeases.length === 0 && (
          <Alert alertType={Alerts.INFO}>No cease requests match the selected type.</Alert>
        )}

        {!isLoading && customerId && filteredCeases.length > 0 && (
          <>
          <div className="table-responsive">
            <table className="table mb-0">
              <thead>
                <tr>
                  <th>Cease reference</th>
                  <th>Type</th>
                  {showAllCustomers && <th className="customer-name-col">Customer</th>}
                  <th>Customer reference</th>
                  <th>IDN</th>
                  <th>Requested cease date</th>
                  <th>Submitted by</th>
                  <th>Submitted date</th>
                  <th className="cease-reason-col">Cease reason</th>
                  {isSSEAdmin && <th>Integration</th>}
                  {isSSEAdmin && <th>Status</th>}
                </tr>
              </thead>
              <tbody>
                {filteredCeases.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to={ceaseById(c.id)} state={{ customerId: c.customerId }}>{(c as any).snowReference || '{Pending}'}</Link>
                    </td>
                    <td>{getTypeLabelFromCategory(c.category, c.subCategory)}</td>
                    {showAllCustomers && <td className="customer-name-col">{c.customerName}</td>}
                    <td title={c.customerReference || ''}>{formatCustomerReference(c.customerReference)}</td>
                    <td>{c.idn}</td>
                    <td>{formatDate(c.requestedCeaseDate)}</td>
                    <td>{getSubmittedByDisplayName(c as any, includedIndex)}</td>
                    <td>{formatDate(c.submittedAt)}</td>
                    <td className="cease-reason-col">{c.ceaseReason}</td>
                    {isSSEAdmin && <td>{(c as any).integrationStatus || 'Not synced'}</td>}
                    {isSSEAdmin && <td className="text-right">
                      <CeaseState state={c.status} />
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="list-footer mt-3">
            <div className="pagination-wrap">
              <ServerSidePaginationBar paging={paging} />
            </div>

            <Button
              onClick={exportAsCsv}
              mainStyle={ButtonMainStyle.LinkPrimary}
              disabled={isExporting || ceases.length === 0}
              className="cease-export-btn"
            >
              {isExporting ? 'Exporting...' : 'Export as CSV'}
            </Button>

          </div>

          {exportError && <Alert alertType={Alerts.DANGER} className="mt-3">{exportError}</Alert>}

          <Alert alertType={Alerts.INFO} className="mt-3 mb-4">
              Neos Cease team will be in touch within 48 hours, 
              the title of this communication will contain the 
              Cease reference relating to your cease request.
          </Alert>

          </>
        )}
      </Container>
    </HeaderWithChildrenWrapper>
  );
};

const Container = styled.div`
  .cease-controls {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    flex-wrap: wrap;
    column-gap: 1rem;
    row-gap: 0.75rem;
  }

  .cease-showAllCustomers {
    margin-right: 2rem;
    margin-bottom: 0.5rem;
  }
    .cease-showAllCustomers label {
      margin-bottom: 0;
    }

  .cease-type-filter {
    min-width: 220px;
  }

  .cease-export-btn {
    margin-right: 0;
    margin-bottom: 0;
  }

  .list-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .pagination-wrap {
    flex: 1;
    min-width: 320px;
  }

  table {
    margin-bottom: 0;
    font-size: 1rem;
  }

  table th,
  table td {
    white-space: nowrap;
  }

  table th.cease-reason-col,
  table td.cease-reason-col {
    white-space: normal;
  }

  table th.customer-name-col,
  table td.customer-name-col {
    white-space: normal;
  }
`;

export default CeaseList;
